/**
 * Leaflet helpers shared by the portal's maps (the lines-remaining map, the Operations Hub): Teslyn's base maps
 * (`BASE_LAYERS`, `tile-cache.ts`), path styles from the brand's CSS variables read at runtime (so themes apply), and
 * text nodes for tooltips and popups (text from data never reaches the map as markup). A map's box carries
 * `data-basemap` (decision 654), so its base-map colours take their imagery or light value; every line is drawn over
 * its thin dark edge (`shapesOf`).
 */
import L from 'leaflet';
import { currentRoadsTiles } from './roads.ts';
import {
  BASE_LAYERS,
  dashOf,
  providerDef,
  strokeStack,
  SYMBOL_PX,
  symbolSvg,
  type BaseLayerId,
  type FeatureStyle,
  type MapFeature,
  type MapStroke,
  type TileProviderId,
} from '@teslyn/domain';

/**
 * The browser's addresses of Teslyn's keyless tile providers: the same as `crates/teslyn-core/src/map/tiles.rs`
 * (Teslyn Field fetches them through its tile cache; the browser asks them directly).
 */
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const TILE_URL: Readonly<Partial<Record<TileProviderId, string>>> = {
  'esri-imagery':
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  'esri-boundaries':
    'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
  'esri-topo':
    'https://server.arcgisonline.com/arcgis/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
  'osm-standard': 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  opentopomap: 'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
};

/** The base maps the browser can show: every Teslyn base map whose providers it can fetch. */
export const WEB_BASE_LAYERS = BASE_LAYERS.filter((b) => b.providers.every((p) => TILE_URL[p]));

/** A provider's credit for Leaflet's attribution control: its copyright page linked when it has one (OpenStreetMap). */
export function attributionHtml(id: TileProviderId): string {
  const pd = providerDef(id);
  if (!pd.copyright_url) return pd.attribution;
  const name = pd.attribution.replace(/^© /, '').replace(/ contributors$/, '');
  const link = `<a href="${pd.copyright_url}" target="_blank" rel="noopener noreferrer">${name}</a>`;
  return pd.attribution.replace(name, link);
}

/** A base map as Leaflet tile layers, bottom first (Satellite with labels: imagery, then the labels). */
export function baseLayer(id: BaseLayerId): L.LayerGroup {
  const def = BASE_LAYERS.find((b) => b.id === id) ?? BASE_LAYERS[0]!;
  return L.layerGroup(
    def.providers.map((p) => {
      const pd = providerDef(p);
      const roads = currentRoadsTiles();
      // N19: Roads from the organisation's host when it set one
      const custom = p === 'osm-standard' && roads.custom;
      return L.tileLayer(custom ? roads.template : TILE_URL[p]!, {
        maxZoom: def.maxZoom,
        maxNativeZoom: pd.maxNativeZoom,
        attribution: custom ? escapeHtml(roads.attribution) : attributionHtml(p),
      });
    }),
  );
}

export const css = (name: string): string =>
  typeof document === 'undefined'
    ? ''
    : getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();

export type Css = (name: string) => string;

/** The tokens as an element sees them (a map's box: its base map), each read once. Make one per drawing. */
export function elementCss(el: Element | null): Css {
  if (!el || typeof getComputedStyle === 'undefined') return css;
  const style = getComputedStyle(el);
  const seen = new Map<string, string>();
  return (name) => {
    let v = seen.get(name);
    if (v === undefined) {
      v = style.getPropertyValue(`--${name}`).trim();
      seen.set(name, v);
    }
    return v;
  };
}

const STROKE_TOKEN: Readonly<Record<MapStroke, string>> = {
  thin: 'map-stroke-thin',
  normal: 'map-stroke',
  thick: 'map-stroke-thick',
};

export const colourOf = (c: FeatureStyle['colour'], read: Css = css): string =>
  typeof c === 'string' ? read(c) : c.css;

const num = (v: string, fallback: number): number => {
  const x = Number.parseFloat(v);
  return Number.isFinite(x) ? x : fallback;
};

const weightOf = (s: MapStroke, read: Css): number => num(read(STROKE_TOKEN[s]), 2);

/** Leaflet path options of a feature style, from the tokens. */
export function pathOptions(style: FeatureStyle, read: Css = css): L.PathOptions {
  const colour = colourOf(style.colour, read);
  const weight = weightOf(style.stroke, read);
  return {
    color: colour,
    weight,
    ...dashOf(style, weight),
    fill: style.fill > 0,
    fillColor: style.fill_colour ? colourOf(style.fill_colour, read) : colour,
    fillOpacity: style.fill,
  };
}

/**
 * A line or polygon of the map and the paths under it, bottom first (decision 654): the glow of a line ticked for the
 * plan and the thin dark edge, which take no pointer, then the shape itself. Points are drawn as circles, nothing
 * under them; a point whose style names a symbol (a fire, a hot spot, a tower; `map-symbols.ts`, R143 and R149; a
 * patch's or reflight's direction arrow, decision 1011) is a marker carrying the symbol's SVG in its colour, as
 * Teslyn Field draws it.
 */
export function shapesOf(
  f: MapFeature,
  read: Css = css,
  extra: L.PathOptions = {},
): { readonly under: L.Path[]; readonly main: L.Path | L.Marker } {
  if (f.geometry.type === 'point' && f.style.symbol) {
    const px = f.style.symbol_large ? SYMBOL_PX.large : SYMBOL_PX.normal;
    return {
      under: [],
      main: L.marker([f.geometry.at.lat, f.geometry.at.lng], {
        // a direction arrow turned along its line over the lines' dark edge (decision 1011)
        icon: L.divIcon({
          html: symbolSvg(f.style.symbol, colourOf(f.style.colour, read), px, {
            ...(f.style.rotation_deg !== undefined ? { rotateDeg: f.style.rotation_deg } : {}),
            ...(f.style.casing ? { edge: read('map-line-casing') } : {}),
          }),
          className: 'map-symbol-marker',
          iconSize: [px, px],
          iconAnchor: [px / 2, px / 2],
        }),
        keyboard: false,
        ...(extra.pane ? { pane: extra.pane } : {}),
      }),
    };
  }
  if (f.geometry.type === 'point') {
    return {
      under: [],
      main: L.circleMarker([f.geometry.at.lat, f.geometry.at.lng], {
        ...pathOptions(f.style, read),
        ...extra,
        radius: num(read('map-point-radius'), 6),
        fill: true,
      }),
    };
  }
  const geometry = f.geometry;
  const make = (opts: L.PolylineOptions): L.Path =>
    geometry.type === 'line'
      ? L.polyline(
          geometry.points.map((p) => [p.lat, p.lng] as L.LatLngTuple),
          opts,
        )
      : L.polygon(
          geometry.rings.map((r) => r.map((p) => [p.lat, p.lng] as L.LatLngTuple)),
          opts,
        );
  const sizes = {
    weight: weightOf(f.style.stroke, read),
    casingWidth: num(read('map-casing-width'), 0),
    casingOpacity: num(read('map-casing-opacity'), 0),
    glowWidth: num(read('map-glow-width'), 0),
    glowOpacity: num(read('map-glow-opacity'), 0),
    fadeOpacity: num(read('map-fade-opacity'), 1),
  };
  const under: L.Path[] = [];
  let main: L.Path | null = null;
  for (const s of strokeStack(f.style, sizes, 1)) {
    const opts: L.PolylineOptions = {
      ...extra,
      color: colourOf(s.colour, read),
      weight: s.weight,
      opacity: s.opacity,
      dashArray: s.dashArray,
      lineCap: s.lineCap,
    };
    if (s.role === 'line') {
      main = make({
        ...opts,
        fill: f.style.fill > 0,
        fillColor: f.style.fill_colour ? colourOf(f.style.fill_colour, read) : opts.color!,
        fillOpacity: f.style.fill,
      });
    } else under.push(make({ ...opts, fill: false, interactive: false }));
  }
  return { under, main: main! };
}

export function textNode(text: string, className?: string): HTMLElement {
  const el = document.createElement('span');
  if (className) el.className = className;
  el.textContent = text;
  return el;
}
